// Outbound Marketing & Sales Engine — frontend-first local types.
//
// This is a journey-iteration feature with no backend yet. These shapes are
// authored here (NOT generated from api-types) and consumed by the mock
// adapter and the marketing pages. When the backend lands, codegen types
// replace these. See docs/features/outbound-marketing-engine/01_product_brief.md.

import type { ListOptions, ListResponse } from "./adapter";

// ----- Channels ------------------------------------------------------------

/** The four first-class channels. All behave uniformly to the campaign layer. */
export type MarketingChannel = "email" | "call" | "enclosure" | "direct_mail";

/** Consent is per-channel; "phone" governs the call channel. */
export type ConsentChannel = "email" | "phone" | "direct_mail" | "enclosure";

export type ConsentState = "allowed" | "suppressed";

/** Mirrors the backend's ConsentSource enum
 *  (marketing_contacts_module.models.enums) — kept in sync by hand since
 *  this file predates codegen for the marketing surface. */
export type ConsentSource =
  | "import"
  | "manual"
  | "mailchimp_unsubscribe"
  | "mailchimp_bounce"
  | "landing_page";

// ----- People & companies --------------------------------------------------

/** Audience-member lifecycle. */
export type PersonLifecycle = "suspect" | "prospect" | "customer";

/** Per-person, per-channel consent snapshot (latest state). */
export interface ConsentRecord {
  channel: ConsentChannel;
  state: ConsentState;
  source: ConsentSource;
  reason: string | null;
  /** ISO timestamp of the most recent change to this channel's state. */
  changed_at: string;
}

export interface MarketingCompany {
  id: string;
  name: string;
  market: string;
  /** Set when this company has been deliberately linked to a Tharstern customer. */
  tharstern_customer_id: string | null;
  /** Convenience: number of people under this company. */
  person_count: number;
}

export interface MarketingPerson {
  id: string;
  name: string;
  job_title: string | null;
  email: string | null;
  phone: string | null;
  lifecycle: PersonLifecycle;
  /** Null for standalone (cold suspect) people. */
  company_id: string | null;
  company_name: string | null;
  /** Latest consent state per channel. */
  consent: ConsentRecord[];
  /** Marketing lifecycle status. Null for CSV-imported people (mart-only field). */
  customerStatus:
    "never_ordered" | "new" | "existing" | "lapsed" | "reactivated" | null;
  /**
   * Contact tier. Null means either no enrichment row yet or no
   * tharstern_contact_id link at all (e.g. every CSV-imported person today) —
   * "unknown" is a filter-time bucket over this null field, not a value this
   * carries, mirroring the Contacts page's contact_category contract.
   */
  contactCategory: "strategic" | "key" | "operational" | "unknown" | null;
  /** No longer a reachable contact (left the company, changed role) —
   *  independent of lifecycle and of consent/suppression. */
  isInactive: boolean;
  inactiveReason: string | null;
}

// ----- Audiences -----------------------------------------------------------

/** A single inclusion/exclusion rule over an attribute. */
export interface AudienceRule {
  id: string;
  kind: "include" | "exclude";
  /** A path from the backend's attribute catalog (listAudienceAttributeCatalog),
   *  e.g. "lifecycle_stage", "is_suppressed", "account.customer_status". Kept
   *  as a plain string: a path the page does not know must round-trip as
   *  itself, never be coerced. */
  attribute: string;
  /** One of the attribute's catalog operators ("eq", "neq", "in", "not_in",
   *  "contains", "not_contains", "gte", "lte") — a plain string for the
   *  same round-trip reason. */
  operator: string;
  value: string;
}

export interface AudienceSummary {
  id: string;
  name: string;
  description: string;
  /** People resolved into the audience right now. Not returned by list endpoints — use the preview endpoint. */
  member_count?: number;
  /** Members currently suppressed on at least one channel. Not returned by list endpoints — use the preview endpoint. */
  suppressed_count?: number;
  /** Campaigns currently targeting this audience. Not returned by list endpoints. */
  campaign_count?: number;
  updated_at: string;
}

/** Full audience for the builder — rules + live preview + sample members. */
export interface AudienceDetail extends AudienceSummary {
  rules: AudienceRule[];
  /** Manually pinned-in person ids (override rules). */
  manual_includes: string[];
  /** Manually pinned-out person ids (override rules). */
  manual_excludes: string[];
  preview: AudiencePreview;
  /** A representative sample of resolved members for the preview panel. */
  sample_members: MarketingPerson[];
  /** Default-on opt-out toggle: excludes contacts who've bounced on 2+ prior campaigns. */
  exclude_repeat_bouncers: boolean;
}

/** Live in/out/suppressed counts shown on the builder's right panel. */
export interface AudiencePreview {
  in_count: number;
  out_count: number;
  suppressed_count: number;
}

export interface AudienceListOptions extends ListOptions {
  /** Substring match on name/description. */
  q?: string;
}

// ----- Campaigns & cadence -------------------------------------------------

export type CampaignStatus =
  | "draft"
  | "active"
  | "closed"
  | "archived"
  /** Historical only, from before "Import legacy campaign" was reworked
   * into "Import legacy send" (which attaches onto an existing project
   * instead) — never activatable, never re-sendable. No path sets this
   * going forward. */
  | "legacy";

/** A single cadence step's own lifecycle — narrower than CampaignStatus: a
 *  step never needs `archived` or `legacy` (whole-project decisions) and
 *  never sits "blocked" (activate_step raises rather than silently
 *  blocking). Structurally a subset of CampaignStatus's literals, so a
 *  StepStatus is freely assignable wherever CampaignStatus is expected
 *  (e.g. campaignStatusVariant, badge rendering). */
export type StepStatus = "draft" | "active" | "closed";

/** Timing for a cadence step — a day offset or a named trigger. */
export interface StepTiming {
  kind: "offset" | "trigger";
  /** Days after campaign start; present when kind = "offset". */
  offset_days?: number;
  /** e.g. "next_despatch"; present when kind = "trigger". */
  trigger?: string;
  /** Human label, e.g. "Day 0", "Day 7", "Next despatch". */
  label: string;
}

export interface CadenceStep {
  id: string;
  /** 1-based ordinal in the cadence. */
  order: number;
  channel: MarketingChannel;
  timing: StepTiming;
  /** Content variant label, e.g. "'Spring offer' (Template A)". */
  variant: string;
  /** Per-step consent gate — which channel's consent must be allowed to send. */
  consent_gate: ConsentChannel;
  /** People who will receive this step (consent allowed). */
  will_send_count: number;
  /** People skipped because the gated channel is suppressed. */
  skipped_count: number;
  /** This step's own execution lifecycle — "Send" (an email-channel step)
   *  is the business/UI term; call/enclosure/direct_mail steps use the same
   *  status field but are never called a "Send". Draft/Live/Complete in the
   *  UI, using the same words as the project's own rollup_status. */
  status: StepStatus;
  /** Mailchimp's own campaign id, pasted in once studio has sent this
   *  step's real email. Per-step, never shared across steps — see the
   *  campaign-projects feature. Null until then. */
  mailchimp_campaign_id: string | null;
  /** A mailing's or call round's own name ("Mailing 1 – TIME pack"); null
   *  on email steps, whose label is derived ("Tranche 2"). */
  name: string | null;
  /** The tiers this step goes to. Empty on an email tranche means everyone;
   *  empty on a mailing or call round means nobody yet. */
  target_tier_ids: string[];
  /** The step whose reached people this step follows up, on any channel.
   *  An email step following an email step is a "Reminder"; any other
   *  follow-up reads "Follow-up to <step>". Null on a step that follows
   *  nothing. */
  follows_step_id: string | null;
  /** The date this step is meant to go out on — settable on any draft step
   *  except an email Reminder. Null until set. A Reminder's own effective
   *  date is always derived (see the campaign-scheduling calendar), never
   *  stored here. */
  scheduled_send_date: string | null;
  /** ISO timestamp this step was activated (tagged in Mailchimp) — null
   *  until then. */
  activated_at: string | null;
  /** This step's real Mailchimp send date, from its own engagement summary
   *  — distinct from activated_at (when CIS itself recorded it), which can
   *  drift by days for older or legacy-imported steps. Null until the step
   *  has been engagement-polled at least once. */
  sent_at: string | null;
  /** An email Reminder's derived send date (ISO date): its original
   *  tranche's real send date, else its planned date, plus the reminder's
   *  gap — the date CIS activates it for. Null/absent when unknown or not a
   *  Reminder. */
  reminder_send_date?: string | null;
  /** How many people the step's frozen membership holds. An email send with
   *  none can't be activated (a Reminder needs its audience generated
   *  first). Null/absent when unknown. */
  member_count?: number | null;
}

/** One send (tranche or reminder) on the campaign scheduling calendar —
 *  read-only, computed entirely server-side. */
export type CalendarEntryStatus =
  "sent" | "scheduled" | "needs_attention" | "pending";

export interface CalendarEntry {
  id: string;
  campaign_id: string;
  campaign_name: string;
  /** "Tranche N" / "Tranche N Reminder", or the plain campaign name for a
   *  single-tranche project. */
  send_label: string;
  /** Which kind of step: a send (email), a mailing, a call round. */
  channel: import("./api-types").CadenceChannel;
  /** The step's plan state: a mailing reads Planned (draft / ready) until
   *  it is posted. */
  state: import("./api-types").PlanStepState;
  /** Confirmed real Mailchimp date (a posted mailing: the day it was
   *  posted) if known, else the best estimate. Null only when there's
   *  genuinely no date to show yet. */
  send_date: string | null;
  is_date_confirmed: boolean;
  status: CalendarEntryStatus;
  /** True exactly when the backend would accept a new send_date for this
   *  entry (a draft tranche, not a reminder, not on a legacy campaign) —
   *  mirrors update_cadence_step's own acceptance rule. Gates whether the
   *  calendar offers it as draggable. */
  can_reschedule: boolean;
  /** Populated only for status="needs_attention". */
  attention_reason: string | null;
}

/** "New campaign". Omitted fields take the backend defaults. */
export interface CreateCampaignBody {
  name: string;
  audience_id: string;
  goal: import("./api-types").CampaignGoal;
  description?: string | null;
  start_date?: string;
  end_date?: string;
  /** Number of email sends to split the audience across. Defaults to 1 (a
   *  plain single-send campaign). */
  tranche_count?: number;
  /** Project-wide: generate an automatic reminder send for each tranche. */
  reminders_enabled?: boolean;
  /** "mailing" starts the campaign with one draft mailing (no tranches);
   *  tranche_count and reminders_enabled are then not allowed. */
  starts_with?: "email" | "mailing";
  /** Mailing start only: its name (default "Mailing 1") and planned date. */
  mailing_name?: string;
  mailing_date?: string;
}

export interface CampaignSummary {
  id: string;
  name: string;
  status: CampaignStatus;
  /** What the campaign is for, chosen on creation. Null on campaigns created
   *  before goals existed. */
  goal: import("./api-types").CampaignGoal | null;
  /** Optional free text about the campaign. */
  description: string | null;
  owner: string;
  /** ISO date the campaign window opens. */
  window_start: string;
  /** ISO date the campaign window closes. */
  window_end: string;
  audience_id: string;
  audience_name: string;
  /** Resolved audience member count at last preview. Not returned by list endpoints. */
  member_count?: number;
  suppressed_count?: number;
  step_count: number;
  /** The actual Mailchimp send time (from its campaign report), not the CIS
   *  activation window. Null until the campaign has sent and engagement has
   *  been pulled at least once. */
  mailchimp_send_time: string | null;
  /** Number of sends (email-channel cadence steps) the audience was split
   *  across at creation. A plain single-send campaign is tranche_count=1 —
   *  nothing new is forced on the simple case. */
  tranche_count: number;
  /** Project-wide toggle: a reminder send is generated for each tranche
   *  (not a per-tranche choice). */
  reminders_enabled: boolean;
  /** Denormalized project-level rollup, recomputed server-side whenever a
   *  send's own status changes: "draft" until any send has started, "active"
   *  (UI: Live) once any send has started but not every send is complete,
   *  "closed" (UI: Complete) once every send is complete. Distinct from
   *  `status`, which carries the project's own whole-project lifecycle
   *  (archived/legacy). */
  rollup_status: CampaignStatus;
  /** Optional short keyword list naming the specific product this campaign
   *  promotes (e.g. ["advent", "advent calendar"]). When set, the
   *  commercial-outcomes table/drawer scope their counts to orders whose
   *  Tharstern job description/type match one of these — see
   *  CampaignCommercialOutcome. Null/empty means unscoped (today's default). */
  product_keywords: string[] | null;
  /** Optional product categories this campaign promotes (e.g. merch and
   *  clothing). When set, the Results page counts only orders and quotes in
   *  one of these categories — combined with product_keywords, an order must
   *  match both. Null/empty means unscoped. */
  marketing_categories: MarketingCategory[] | null;
}

export interface CampaignDetail extends CampaignSummary {
  steps: CadenceStep[];
}

/** Last Mailchimp sync outcome for a campaign's email steps. */
export interface MailchimpSyncState {
  status: "idle" | "syncing" | "ok" | "failed";
  last_synced_at: string | null;
  last_error: string | null;
  /** Contacts Mailchimp itself rejected (e.g. invalid email) and skipped — the
   * rest of the audience still syncs; `status` stays "ok" even when this is
   * non-zero. */
  skipped_count: number;
}

export interface EmailPreview {
  step_id: string;
  html_content: string;
  fetched_at: string;
}

export interface CampaignListOptions extends ListOptions {
  status?: CampaignStatus;
}

// ----- Marketing people list -------------------------------------------------

export interface MarketingPeopleListOptions extends ListOptions {
  /** Substring match on person name/email. */
  q?: string;
  hasEmail?: boolean;
  hasPhone?: boolean;
  /** Marketing lifecycle status — mirrors the Contacts page's customer_status filter. */
  customerStatus?:
    "never_ordered" | "new" | "existing" | "lapsed" | "reactivated";
  /** Account segment (strategic/key/client) — mirrors the Contacts page's segment filter. */
  segmentId?: string;
  /** Contact tier — mirrors the Contacts page's contact_category filter. */
  contactCategory?: "strategic" | "key" | "operational" | "unknown";
  /** Inactive contacts are excluded by default; set true to reveal them. */
  includeInactive?: boolean;
}

// ----- Results matrix ------------------------------------------------------

/** One channel cell in the results matrix. */
export interface ResultsCell {
  channel: MarketingChannel;
  /** Short status/touch label, e.g. "134 sent", "60 tickets", "CSV ✓", "—". */
  label: string;
  /** Whether this channel was active in the campaign. */
  active: boolean;
}

export interface ResultsRow {
  campaign_id: string;
  campaign_name: string;
  status: CampaignStatus;
  /** The real "has it started sending" signal — status stays "draft" for
   *  an ongoing project's whole life, so the UI badge renders this, not
   *  status. */
  rollup_status: CampaignStatus;
  audience_name: string;
  cells: ResultsCell[];
}

export interface MarketingResults {
  channels: MarketingChannel[];
  rows: ResultsRow[];
}

// ----- Campaign engagement (Mailchimp) --------------------------------------
//
// Sourced from the marketing-data-visibility backend feature
// (GET /views/campaigns/{id}/engagement). Backend-first — these mirror the
// real response shape, but stay hand-authored here rather than generated
// until codegen is rerun against the live schema.

/** Mailchimp's own aggregate numbers for one send (cadence step) — never
 *  re-derived from the per-contact rows below, so a partial detail pull can
 *  never silently understate the headline rate. Rates are 0–1 fractions.
 *  Keyed by step_id, not campaign_id — a multi-tranche project's Mailchimp
 *  linkage (and therefore its engagement data) lives on the step, since
 *  every send is tracked independently (see campaign-projects). */
export interface CampaignEngagementSummary {
  step_id: string;
  emails_sent: number;
  delivered_count: number;
  delivered_rate: number;
  opens_count: number;
  unique_opens_count: number;
  open_rate: number;
  clicks_count: number;
  unique_clicks_count: number;
  click_rate: number;
  bounces_count: number;
  bounce_rate: number;
  unsubscribes_count: number;
  unsubscribe_rate: number;
  /** ISO timestamp of the last successful Mailchimp pull. */
  last_synced_at: string;
}

/** One contact's engagement footprint — the row a sales rep scans to decide
 *  who's worth calling. No "worth calling" flag here; the rep decides. */
export interface CampaignEngagementContact {
  /** Null if the Mailchimp email never resolved to a known CIS contact. */
  person_id: string | null;
  email: string;
  delivered: boolean;
  opened_count: number;
  clicked_count: number;
  /** Distinct URLs clicked; query strings are stripped before this leaves
   *  the backend (personalized-link caveat — see the campaign-results
   *  product brief; not yet confirmed safe to show verbatim). */
  clicked_links: string[];
  bounced: boolean;
  unsubscribed: boolean;
  /** Null if unresolved: no person_id, no assigned rep, or the rep has no name on file. */
  sales_rep_name: string | null;
  /** Null if unresolved: no person_id, or the contact has no phone on file. */
  phone: string | null;
  /** Null if unresolved: no person_id, or the contact has no company linked. */
  company_name: string | null;
}

export interface CampaignEngagement {
  step_id: string;
  summary: CampaignEngagementSummary;
  contacts: CampaignEngagementContact[];
  last_synced_at: string | null;
}

/** A sent Mailchimp campaign matching a title/subject-line search — lets a
 *  user find the real Mailchimp API campaign id (never surfaced directly in
 *  Mailchimp's own UI) by name instead of guessing from a share-link URL. */
export interface MailchimpCampaignMatch {
  mailchimp_campaign_id: string;
  title: string;
  subject_line: string;
  send_time: string | null;
  /** The number in a Mailchimp report link (`?id=`); not the API id. */
  web_id: number | null;
  /** "sent", or "sending" for an ongoing automation-flow email. */
  status: string;
}

/** Attribution window options for commercial-outcome reporting (days after campaign end). */
export type CommercialOutcomeWindowDays = 5 | 7 | 10 | 14 | 30 | 45 | 90;

/** "<mailing> + follow-up" (2026-10-15): the customers (company level) who
 *  got a posted mailing and then follow_up_step_id as their next send, and
 *  that send's credited orders and quotes from them — from the follow-up's
 *  day up to the day before their next send (or the window end). A cut of
 *  the follow-up send's own figures, never extra credit: it is already
 *  inside that send's row and the campaign totals, so never sum it. */
export interface MailingFollowUpOutcome {
  type: "mailing_follow_up_outcome";
  follow_up_step_id: string;
  /** Distinct customers who got the mailing and then this send next. */
  customer_count: number;
  order_count: number;
  sales_amount: number;
  new_category_customers: number;
  reactivated_customers: number;
  enquired_customer_count: number;
}

/** One sent step's share of its project's commercial outcome. Orders and
 *  quotes are credited to the most recent send each customer had received
 *  before placing them, so per-send order_count / sales_amount /
 *  reactivated_customers sum exactly to the project's, and per-send
 *  new_clicker_count sums to its unique_clicker_count.
 *  enquired_customer_count and new_category_customers are distinct customers
 *  per send, so they can sum to more than the project's. See
 *  docs/features/campaign-results-enrichment/11_per_send_attribution.md. */
export interface CampaignSendOutcome {
  type: "campaign_send_outcome";
  step_id: string;
  /** 1-based ordinal in the project's cadence. */
  position: number;
  channel: MarketingChannel;
  /** The step this one follows up (on a Reminder, the tranche it follows). */
  follows_step_id: string | null;
  /** A mailing's or call round's own name; null on email steps (labelled
   *  client-side). */
  step_name: string | null;
  /** The tiers the step went to; empty for a send to everyone or a
   *  follow-up. */
  target_tier_names: string[];
  /** A mailing's own figures (distinct people): reached when marked posted,
   *  of those the courier packs delivered and the packs returned, and left
   *  out, plus its "+ follow-up" cuts (empty until a later send has gone out
   *  to some of its customers). Null on other channels. */
  mailing: {
    type: "mailing_send_outcome";
    posted: number;
    delivered: number;
    returned: number;
    not_included: number;
    follow_ups: MailingFollowUpOutcome[];
  } | null;
  /** A call round's own figures: people a call ticket was raised for, and
   *  the outcomes recorded on those tickets (CallOutcome -> count; {} while
   *  none). Null on other channels. */
  call_round: {
    type: "call_round_send_outcome";
    call_tickets: number;
    outcome_mix: Record<string, number>;
  } | null;
  /** ISO timestamp of this send: Mailchimp's own send time, falling back to
   *  its first executed touch, then activated_at (the workspace's basis). */
  sent_at: string | null;
  contacts_sent: number;
  order_count: number;
  /** Sum of booking_amt over the orders credited to this send. */
  sales_amount: number;
  /** Distinct customers with a first-in-category order credited to this send. */
  new_category_customers: number;
  /** Distinct customers lapsed as of the day they were sent this step who
   *  then placed an order credited to it. A customer is reactivated by at
   *  most one send. */
  reactivated_customers: number;
  enquired_customer_count: number;
  /** Distinct clicking contacts on this send, by email. */
  unique_clicker_count: number;
  /** Clickers who hadn't clicked any earlier send of this project. */
  new_clicker_count: number;
  /** Clickers who had clicked an earlier send of this project. */
  repeat_clicker_count: number;
  /** First-ever orders in a category credited to this send (orders, not
   *  customers) — the Performance tab's "New services". */
  new_category_order_count: number;
  /** This send's normal ordering: each recipient's normal split over the
   *  days this send owns for them (the same intervals that split Sales), so
   *  a campaign's sends add up to its own. Null when not computable. */
  normal_ordering_amount: number | null;
  uplift_amount: number | null;
  uplift_pct: number | null;
  /** The campaign's "not a fair comparison" rule on this send's own
   *  customers. */
  sent_customer_count: number;
  prior_year_buyer_count: number;
  uplift_is_comparable: boolean;
}

/** Were a campaign's tickets worked, and did the customers a rep reached
 *  out to order? A ticket is *actioned* when a person did more than route it
 *  (dbt ticket_followthrough). A customer is *reached* when one of its
 *  contacts' tickets for this campaign was actioned; an order is *after
 *  outreach* when it is one of the campaign's credited orders, booked on or
 *  after that first action. reached_* / not_reached_* cover only customers
 *  with a ticket for this campaign. */
export interface CampaignRepOutreach {
  type: "campaign_rep_outreach";
  tickets_raised: number;
  tickets_actioned: number;
  outreach_order_count: number;
  outreach_sales_amount: number;
  outreach_enquired_customer_count: number;
  reached_customer_count: number;
  reached_ordered_customer_count: number;
  not_reached_customer_count: number;
  not_reached_ordered_customer_count: number;
}

/** One tier's funnel across a campaign's steps: its people, and how many
 *  of them were posted a mailing (and of those, had a courier pack
 *  delivered or a pack returned), emailed and called (distinct people). */
export interface CampaignTierOutcome {
  type: "campaign_tier_outcome";
  tier_id: string;
  tier_name: string;
  people: number;
  posted: number;
  delivered: number;
  returned: number;
  emailed: number;
  called: number;
  /** What came of the tier's campaign tickets (CallOutcome -> count). */
  outcome_mix: Record<string, number>;
}

export interface CampaignCommercialOutcome {
  /** Discriminates this row from LeadSourceCommercialOutcome in the merged
   *  Commercial Outcomes table. */
  type: "campaign_commercial_outcome";
  campaign_id: string;
  campaign_name: string;
  /** "always_on" for a triggered email's campaign: labelled "Always on". */
  campaign_kind: "planned" | "always_on";
  /** What the campaign is for; null on campaigns created before goals. */
  goal: import("./api-types").CampaignGoal | null;
  window_days: CommercialOutcomeWindowDays;
  /** ISO date: the last day sales are counted (end date + window; today's
   *  date for an always-on campaign). Sales are "so far" until it passes. */
  window_end_date: string;
  /** The campaign's own product_keywords, echoed back so the table/drawer can
   *  show these counts as product-scoped rather than "any order". Null/empty
   *  means every count below is unscoped. */
  product_keywords: string[] | null;
  /** The campaign's own marketing_categories, echoed back the same way —
   *  null/empty means no category scope. */
  marketing_categories: MarketingCategory[] | null;
  /** Orders placed by the cohort within the window and credited to one of
   *  the project's sends — correlation, not attributed causation. */
  order_count: number;
  /** Sum of booking_amt over those orders. */
  sales_amount: number;
  /** Cohort customers whose lifetime-first order in some category fell inside the window. */
  new_category_customers: number;
  /** First-ever orders in a category inside the window (orders, not
   *  customers) — the Performance tab's "New services". */
  new_category_order_count: number;
  /** Cohort customers lapsed as of the send that preceded their order, who
   *  ordered inside the window — the sum of the per-send figures. */
  reactivated_customers: number;
  /** Cohort customers with a matching quote in the window, any status — a superset of
   *  order_count by design (every order started as a quote); the gap between the two is
   *  "quoted but didn't convert". */
  enquired_customer_count: number;
  /** Distinct clicking contacts across all the project's sends — a contact
   *  who clicked a tranche and its reminder counts once, unlike Mailchimp's
   *  own per-send unique-clicks figure. */
  unique_clicker_count: number;
  /** "Normal ordering": what the sent customers would typically have
   *  ordered over the same period without the campaign — each customer's own
   *  previous 12 months, scaled by how customers who weren't emailed did over
   *  the same days. Null when not computable. See
   *  12_normal_ordering_uplift.md. */
  normal_ordering_amount: number | null;
  /** sales_amount − normal_ordering_amount; null when normal is null. */
  uplift_amount: number | null;
  /** Uplift as a percentage of normal (19 = +19%); null when normal is 0 or
   *  not computable. */
  uplift_pct: number | null;
  /** Sent customers with less than a year's ordering history — their normal
   *  figure is low or 0, so their orders mostly count as uplift. */
  short_history_customer_count: number;
  /** The comparison group's size: customers who weren't sent the campaign
   *  with an order (same product scoping) in the 365 days before it. Normal
   *  ordering scales the recipients' own history by how these customers did,
   *  so a small group means a weak comparison. */
  comparison_customer_count: number;
  /** Distinct customers the campaign was sent to. */
  sent_customer_count: number;
  /** Of those, how many had ordered anything in the 12 months before their
   *  own first send. */
  prior_year_buyer_count: number;
  /** False when at least 80% of recipients were already buying: the
   *  customers who weren't emailed are then a different kind of customer, so
   *  uplift isn't a fair comparison and isn't shown. */
  uplift_is_comparable: boolean;
  /** Null until the hourly data build first produces the ticket figures
   *  (shown as "—", never as zero). */
  rep_outreach: CampaignRepOutreach | null;
  /** One row per sent step, ordered by send time. */
  sends: CampaignSendOutcome[];
  /** One funnel row per tier (distinct people), in tier order; empty for
   *  an untiered campaign. */
  tiers: CampaignTierOutcome[];
  /** Total sent cohort (touches, excl. skipped) for this campaign. */
  cohort_size: number;
  /** Cohort members with any Tharstern identity match (verified or not). */
  resolved_cohort_size: number;
  /** Cohort members whose identity match came from the mart ingest, not CSV import. */
  verified_cohort_size: number;
}

/** Commercial outcomes attributed to one active lead source (a landing
 *  page), open-ended — no window, unlike CampaignCommercialOutcome: every
 *  order on or after a resolved customer's own submission date counts, with
 *  no upper bound. No enquired/reactivated concepts (a submission itself
 *  *is* the enquiry, and "reactivated" doesn't map onto a landing-page
 *  lead) — see the backend model's docstring. */
/** Coarse product-family taxonomy a lead source's or campaign's promoted
 *  products fall into — the generated backend MarketingCategory enum
 *  (marketing_leads_module). "other" is a valid backend value but
 *  deliberately not offered in the scoping pickers (LandingPagesPage,
 *  CampaignWorkspacePage) — it means "we don't know what this is", never
 *  something a source or campaign is deliberately scoped to. */
export type MarketingCategory = import("./api-types").MarketingCategory;

/** The categories a lead source may be deliberately scoped to: all but
 *  "other". The backend's lead-source request takes only these
 *  (`SelectableMarketingCategory` in marketing_leads_module). */
export type SelectableMarketingCategory = Exclude<MarketingCategory, "other">;

export function isSelectableMarketingCategory(
  category: MarketingCategory,
): category is SelectableMarketingCategory {
  return category !== "other";
}

export const SELECTABLE_MARKETING_CATEGORIES: {
  value: SelectableMarketingCategory;
  label: string;
}[] = [
  { value: "clothing", label: "Clothing & workwear" },
  { value: "merch", label: "Merch" },
  { value: "print", label: "Print" },
  { value: "display", label: "Display" },
  { value: "mailing", label: "Mailing" },
  { value: "creative", label: "Creative" },
];

export interface LeadSourceCommercialOutcome {
  /** Discriminates this row from CampaignCommercialOutcome in the merged
   *  Commercial Outcomes table. */
  type: "lead_source_commercial_outcome";
  lead_source_id: string;
  lead_source_name: string;
  product_keywords: string[] | null;
  marketing_categories: MarketingCategory[] | null;
  order_count: number;
  /** Sum of booking_amt over order_count's orders. */
  sales_amount: number;
  new_category_customers: number;
  /** Customers who had ordered before, but not in the year before they
   *  submitted the form, and ordered on or after it. */
  reactivated_customers: number;
  /** How many people have submitted the form for this source — the Results
   *  page's Enquired figure for this row (a submission is the enquiry). */
  submission_count: number;
  /** Submissions that resolved to a known contact — the closest analogue to
   *  a campaign's resolved_cohort_size, with no "verified" concept. */
  resolved_submission_count: number;
}

/** Either row shape the merged Commercial Outcomes table renders — see the
 *  `type` discriminator on each. */
export type CommercialOutcome =
  CampaignCommercialOutcome | LeadSourceCommercialOutcome;

/** One lead source (landing page/lead magnet) — backs the landing pages
 *  list, create, and edit journeys. */
export interface LeadSource {
  id: string;
  slug: string;
  name: string;
  is_active: boolean;
  product_keywords: string[] | null;
  marketing_categories: MarketingCategory[] | null;
  /** Which CRM ticket category a ticket routed from this page is filed
   *  under. Null routes the ticket normally but leaves it uncategorised,
   *  with nothing on it to explain why — see LandingPagesPage's picker.
   *
   *  Present on LeadSourceResponse since the field was added, but missing
   *  from this hand-written interface until the picker needed it, so the
   *  page could not read a value the API had been returning all along. */
  ticket_category_id: string | null;
  /** Who this campaign's enquiries fall back to when no owner can be derived
   *  from the enquirer's contact or their company's portfolio. Null means no
   *  campaign-specific fallback — routing then tries the environment default,
   *  and leaves the ticket genuinely unassigned if that is unset too. */
  default_sales_rep_user_id: string | null;
  created_at: string;
  /** When the page actually went live on the public site — null means
   *  "not live yet" (distinct from created_at, when the CIS record itself
   *  was created, which commonly predates go-live). */
  launched_at: string | null;
}

/** One cohort contact whose account_code matches an order's customer_id.
 *  account_code is a soft, unenforced link, so an order may carry several. */
export interface MatchedContact {
  person_id: string;
  name: string;
  email: string | null;
}

/** One booking behind a campaign's commercial-outcome counts — the drill-in
 *  view of exactly the rows a CampaignCommercialOutcome count summarises. */
export interface CampaignCommercialOutcomeOrder {
  booking_key: string;
  order_booking_date: string;
  customer_id: string;
  customer_name: string | null;
  contacts: MatchedContact[];
  /** Null for call-off draws and misc invoice lines — they have no job. */
  job_no: string | null;
  /** Tharstern's own free-text label (e.g. "Brochures") — finer-grained than marketing_category. */
  job_type: string | null;
  marketing_category: string;
  booking_amt: number;
  is_first_order_in_category: boolean;
  /** True when this order's customer had ordered before, but not within the
   *  lapsed threshold before the day they were sent the send this order is
   *  credited to — same test CampaignCommercialOutcome.reactivated_customers
   *  counts, at row grain. */
  is_reactivated_customer: boolean;
  /** The send (cadence step id) this order is credited to. */
  attributed_step_id: string;
}

/** One booking behind a lead source's commercial-outcome counts — the
 *  campaign order row's shape without attributed_step_id (a landing page
 *  has no sends), plus the submission date the order counts from. */
export interface LeadSourceCommercialOutcomeOrder {
  booking_key: string;
  order_booking_date: string;
  /** The customer's earliest submission to this source; orders count from it. */
  submitted_on: string;
  customer_id: string;
  customer_name: string | null;
  contacts: MatchedContact[];
  job_no: string | null;
  job_type: string | null;
  marketing_category: string;
  booking_amt: number;
  is_first_order_in_category: boolean;
  /** True when this order's customer had ordered before, but not within the
   *  lapsed threshold before they submitted the form. */
  is_reactivated_customer: boolean;
}

/** One quote behind a campaign's enquired_customer_count — the drill-in view
 *  of exactly the rows that count summarises. Status is not filtered: an
 *  open, won or lost quote all count as "enquired". */
export interface CampaignCommercialOutcomeEnquiry {
  estimate_header_ref: string;
  customer_id: string;
  customer_name: string | null;
  contacts: MatchedContact[];
  estimate_date: string;
  description: string | null;
  status: string;
  /** Null for unpriced/abandoned quotes. */
  estimate_amt: number | null;
  marketing_category: string;
  /** The send (cadence step id) this quote is credited to. */
  attributed_step_id: string;
}

// Re-export the list response so consumers can import everything from here.
export type { ListResponse };

/** Triggered emails (docs/features/triggered-emails/): a rule that emails a
 *  contact when something happens to them or their account, sent by a
 *  Mailchimp flow started by the trigger's tag. The shapes are the composed
 *  views' (generated from the backend). */
export type MarketingTriggerSummary =
  import("./api-types").MarketingTriggerSummaryView;
export type MarketingTrigger = import("./api-types").MarketingTriggerView;
/** One account or contact that qualified for a trigger on its daily list. */
export type TriggerCandidate = import("./api-types").TriggerCandidateView;
export type TriggerSentItem = import("./api-types").TriggerSentItemView;
export type TriggerContactOption =
  import("./api-types").TriggerContactOptionView;
export type TriggerStatus = MarketingTriggerSummary["status"];
/** Where a contact sits on a trigger's daily list. */
export type TriggerCandidateState = TriggerCandidate["state"];
/** Why a qualifying contact won't be emailed. Each maps to a plain-English
 *  label (`@shared-core/domain/marketing-triggers`). */
export type TriggerLeftOutReason = NonNullable<
  TriggerCandidate["left_out_reason"]
>;

export interface ApproveTriggerListInput {
  /** "To send" rows the user removed. */
  removed_firing_ids: string[];
  /** For "needs you" rows: row → the contact the user picked. */
  picked_contacts: Record<string, string>;
  /** One per confirmation dialog, so a double click sends once. */
  request_id: string;
}

export interface UpdateMarketingTriggerInput {
  status?: TriggerStatus;
  check_before_sending?: boolean;
  from_name?: string;
  catch_up_days?: number;
}

// ---- Campaign performance (the Results page's Performance tab) ----
// Generated from the backend (GET /views/campaign-performance and the
// marketing campaigns cost / plan-ref / targets routes). See
// docs/features/campaign-performance-targets/.

type Schemas = import("./api-schema.generated").components["schemas"];

/** The org-wide targets every email send is judged against. Fractions
 *  (0.3 = 30%): Opened and Clicked are % of Delivered and must reach their
 *  target; Unsubscribed is % of Contacts and is a maximum. */
export type PerformanceTargets = Schemas["PerformanceTargetsResponse"];

/** One send's, one campaign's or the summary's actual figures. A
 *  campaign's engagement is the sum of its sends' (a person clicking two
 *  sends counts on each); its sales and uplift are its own. Rates, leads,
 *  cost per lead and ROI are derived (`@shared-core/domain/campaign-performance`). */
export type PerformanceFigures = Schemas["PerformanceFigures"];

/** Summary to date: totals across every campaign shown; its uplift adds up
 *  only fair-comparison campaigns (`uplift_excluded_campaign_count` left out). */
export type PerformanceSummary = Schemas["PerformanceSummary"];

export type CampaignCostType = Schemas["CampaignCostType"];

/** A cost the marketing manager entered against a campaign, optionally tied
 *  to one of its sends. Untied costs count at campaign level only. */
export type CampaignCost = Schemas["CampaignPerformanceCost"];

/** What add-cost / update-cost return. */
export type CampaignCostRecord = Schemas["CampaignCostResponse"];

export type CampaignCostInput = Schemas["CampaignCostRequest"];

/** Add-cost takes a request_id (one per dialog open) so a double click
 *  records one cost. */
export type AddCampaignCostInput = Schemas["AddCampaignCostRequest"];

/** One email send; labelled with `sendLabel` from position /
 *  follows_step_id, the same rule as the Commercial outcomes rows. */
export type SendPerformance = Schemas["SendPerformance"];

export type CampaignPerformance = Schemas["CampaignPerformance"];

export type MarketingPerformance = Schemas["MarketingPerformance"];
